/**
 * TanStack Table v9 — zestaw funkcji DataTable i typy kolumn z nim zwiazane.
 *
 * v9 wymaga jawnej listy funkcji (`tableFeatures`) i parametru `TFeatures`
 * w kazdym typie (`ColumnDef<TFeatures, TData, TValue>`). Strony importuja
 * `ColumnDef`/`Column`/`Row` z `@/components/ui/data-table` — tam generyk jest
 * juz zwiazany z tym zestawem, wiec definicje kolumn wygladaja jak w v8.
 *
 * Dwa miejsca utrzymuja zachowanie sprzed migracji (8.21.3):
 *  - `normalizeColumnPinning` — localStorage i schematy kolumn zapisane przez v8
 *    maja pinning `{ left, right }`; v9 czyta tylko `{ start, end }`
 *    i przy starym ksztalcie `getHeaderGroups()` rzuca TypeError.
 *  - `legacyAutoSortFn` + `createLegacySortedRowModel` — wybor funkcji
 *    sortujacej dla kolumn bez `sortFn` dokladnie jak w v8 (patrz nizej).
 */
import {
	type ColumnPinningState,
	columnFilteringFeature,
	columnOrderingFeature,
	columnPinningFeature,
	columnResizingFeature,
	columnSizingFeature,
	columnVisibilityFeature,
	createFilteredRowModel,
	createSortedRowModel,
	metaHelper,
	type RowData,
	rowSortingFeature,
	type Column as TanStackColumn,
	type ColumnDef as TanStackColumnDef,
	type Row as TanStackRow,
	tableFeatures,
} from "@tanstack/react-table";
import type * as React from "react";
import type { MobileColumnRole } from "@/components/ui/table-mobile";

/** Rozszerzenia `columnDef.meta` obslugiwane przez DataTable. */
export interface DataTableColumnMeta {
	isNumeric?: boolean;
	/**
	 * Kolumna `isNumeric` z wartoscia w punktach procentowych (15.58 = 15,58 %).
	 * Eksport Excel zapisuje ja jako procent (`ExcelColumn.percent`), zamiast
	 * liczby, ktora uzytkownik musial przeliczac recznie.
	 */
	isPercent?: boolean;
	isDate?: boolean;
	alwaysLast?: boolean;
	/** When false, the column is shown in GroupByDropdown but disabled (cannot be toggled). */
	groupable?: boolean;
	filterMode?: "smart" | "progressive";
	minChars?: number;
	/** Custom filter hints shown in the tooltip (e.g. ["! — duplikaty", "tekst — szukanie"]) */
	filterHints?: string[];
	/**
	 * Explicit option list for faceted filters. When present, faceted UIs should
	 * render these options instead of deriving them from loaded rows.
	 * Required in server-filtering mode where loaded data is incomplete.
	 */
	facetedOptions?: { label: string; value: string }[];
	/**
	 * Wlasny kontrolka filtra w wierszu filtrow naglowka — dla kolumn, ktore
	 * filtruja sie osobnym paramem backendu (np. hierarchiczna kategoria),
	 * a nie generycznym `columnFilters`. Zwrocony node zastepuje input, wiec
	 * kolumna nie musi byc `enableColumnFilter: false` z martwym polem.
	 */
	filterRender?: () => React.ReactNode;
	/**
	 * Extra classes on the column's header + body cells, e.g. `bg-primary/5`
	 * to visually emphasize key columns (parity with the "Cena koncowa"
	 * column in /strateg-cenowy/wyniki). Ignored on pinned columns (pinned
	 * cells carry their own sticky background) and on selected rows.
	 */
	cellClassName?: string;
	/**
	 * When true, the column stretches to fill remaining horizontal space
	 * (flexGrow: 1). Use on one non-pinned column per table — typically the
	 * primary label column — so all columns collectively fill the full width.
	 */
	fill?: boolean;
	/**
	 * Rola kolumny w mobilnym trybie „Lista” (<md). Bez tego pola dziala
	 * automat z `table-mobile.ts`: pierwsza kolumna tekstowa = tytul, druga =
	 * podtytul, liczbowe = miary, `actions` = stopka szczegolow. `"detail"`
	 * zdejmuje kolumne z wiersza listy (zostaje na ekranie szczegolow).
	 * Kiedy nadpisywac: `references/mobile.md` w farmaster-ui-standard.
	 */
	mobileRole?: MobileColumnRole;
	/** Ta miara jest domyslnie wybrana w przelaczniku miary listy. */
	mobileDefaultMeasure?: boolean;
	/** Krotka etykieta w przelaczniku miary (domyslnie `columnLabels`). */
	mobileLabel?: string;
}

/* ============================================================================
   Sortowanie zgodne z v8
   ============================================================================ */

/** Minimalny ksztalt wiersza potrzebny funkcjom sortujacym i filtrujacym. */
export interface DataTableValueRow {
	getValue: <TValue>(columnId: string) => TValue;
}

type LegacySortFn = (rowA: DataTableValueRow, rowB: DataTableValueRow, columnId: string) => number;

// Ponizsze funkcje sa przeniesione 1:1 z @tanstack/table-core 8.21.3
// (src/sortingFns.ts, licencja MIT). v9 ma wlasna implementacje
// `compareAlphanumeric`, ktora rozni sie w przypadkach brzegowych
// (np. zera wiodace), a kolejnosc w raportach i eksporcie ma zostac ta sama.
const reSplitAlphaNumeric = /([0-9]+)/gm;

function compareBasic(a: unknown, b: unknown): number {
	if (a === b) return 0;
	return (a as number) > (b as number) ? 1 : -1;
}

function legacyToString(a: unknown): string {
	if (typeof a === "number") {
		if (Number.isNaN(a) || a === Number.POSITIVE_INFINITY || a === Number.NEGATIVE_INFINITY) {
			return "";
		}
		return String(a);
	}
	if (typeof a === "string") return a;
	return "";
}

function compareAlphanumeric(aStr: string, bStr: string): number {
	const a = aStr.split(reSplitAlphaNumeric).filter(Boolean);
	const b = bStr.split(reSplitAlphaNumeric).filter(Boolean);
	while (a.length && b.length) {
		const aa = a.shift() as string;
		const bb = b.shift() as string;
		const an = Number.parseInt(aa, 10);
		const bn = Number.parseInt(bb, 10);
		// Celowo `sort()` bez komparatora — tak jak w v8; sluzy tylko do wykrycia NaN.
		const combo = [an, bn].sort();
		if (Number.isNaN(combo[0])) {
			if (aa > bb) return 1;
			if (bb > aa) return -1;
			continue;
		}
		if (Number.isNaN(combo[1])) return Number.isNaN(an) ? -1 : 1;
		if (an > bn) return 1;
		if (bn > an) return -1;
	}
	return a.length - b.length;
}

export const legacySortFns = {
	alphanumeric: ((rowA, rowB, columnId) =>
		compareAlphanumeric(
			legacyToString(rowA.getValue(columnId)).toLowerCase(),
			legacyToString(rowB.getValue(columnId)).toLowerCase(),
		)) as LegacySortFn,
	alphanumericCaseSensitive: ((rowA, rowB, columnId) =>
		compareAlphanumeric(
			legacyToString(rowA.getValue(columnId)),
			legacyToString(rowB.getValue(columnId)),
		)) as LegacySortFn,
	text: ((rowA, rowB, columnId) =>
		compareBasic(
			legacyToString(rowA.getValue(columnId)).toLowerCase(),
			legacyToString(rowB.getValue(columnId)).toLowerCase(),
		)) as LegacySortFn,
	textCaseSensitive: ((rowA, rowB, columnId) =>
		compareBasic(
			legacyToString(rowA.getValue(columnId)),
			legacyToString(rowB.getValue(columnId)),
		)) as LegacySortFn,
	datetime: ((rowA, rowB, columnId) => {
		const a = rowA.getValue<Date>(columnId);
		const b = rowB.getValue<Date>(columnId);
		return a > b ? 1 : a < b ? -1 : 0;
	}) as LegacySortFn,
	basic: ((rowA, rowB, columnId) =>
		compareBasic(rowA.getValue(columnId), rowB.getValue(columnId))) as LegacySortFn,
};

/**
 * Wybor funkcji dla kolumny bez `sortFn`, dokladnie jak `getAutoSortingFn` w v8.
 *
 * v8 probkowal `flatRows.slice(10)` — wiersze OD JEDENASTEGO — wiec tabela
 * z co najwyzej 10 przefiltrowanymi wierszami zawsze sortowala `basic`
 * (surowe porownanie: wielkie litery przed malymi, polskie znaki po "z").
 * v9 probkuje pierwsze 10 wierszy i wybralby `text`/`alphanumeric`.
 */
function pickLegacyAutoSortFn(
	filteredFlatRows: readonly DataTableValueRow[],
	columnId: string,
): LegacySortFn {
	let isString = false;
	for (const row of filteredFlatRows.slice(10)) {
		const value = row?.getValue(columnId);
		if (Object.prototype.toString.call(value) === "[object Date]") return legacySortFns.datetime;
		if (typeof value === "string") {
			isString = true;
			if (value.split(reSplitAlphaNumeric).length > 1) return legacySortFns.alphanumeric;
		}
	}
	return isString ? legacySortFns.text : legacySortFns.basic;
}

interface FilteredRowsSource {
	getFilteredRowModel: () => { flatRows: readonly DataTableValueRow[] };
}

// Kontekst biezacego sortowania: v9 wola komparator synchronicznie wewnatrz
// `getSortedRowModel()`, wiec decyzja `auto` liczy sie raz na kolumne
// i sortowanie, a nie raz na porownanie.
let activeAutoSort: { table: FilteredRowsSource; fns: Map<string, LegacySortFn> } | null = null;

// Decyzje dla wywolan poza `createLegacySortedRowModel` (np. reczne sortowanie
// przez `column.getSortFn()`), kluczowane tablica przefiltrowanych wierszy:
// bez tego kazde porownanie przechodziloby wiersze 11+ — O(n^2 log n) na sort.
const autoSortFnsByRows = new WeakMap<readonly DataTableValueRow[], Map<string, LegacySortFn>>();

function cachedAutoSortFn(rows: readonly DataTableValueRow[], columnId: string): LegacySortFn {
	let byColumn = autoSortFnsByRows.get(rows);
	if (!byColumn) {
		byColumn = new Map();
		autoSortFnsByRows.set(rows, byColumn);
	}
	let fn = byColumn.get(columnId);
	if (!fn) {
		fn = pickLegacyAutoSortFn(rows, columnId);
		byColumn.set(columnId, fn);
	}
	return fn;
}

/**
 * Komparator `auto` zgodny z v8. DataTable przypisuje go kolumnom bez
 * wlasnego `sortFn` (poza `meta.isNumeric`, ktore maja swoj komparator).
 */
export function legacyAutoSortFn(
	rowA: DataTableValueRow & { table: FilteredRowsSource },
	rowB: DataTableValueRow,
	columnId: string,
): number {
	const context = activeAutoSort;
	if (!context) {
		const rows = rowA.table.getFilteredRowModel().flatRows;
		return cachedAutoSortFn(rows, columnId)(rowA, rowB, columnId);
	}
	let fn = context.fns.get(columnId);
	if (!fn) {
		fn = pickLegacyAutoSortFn(context.table.getFilteredRowModel().flatRows, columnId);
		context.fns.set(columnId, fn);
	}
	return fn(rowA, rowB, columnId);
}

/** `createSortedRowModel()` z kontekstem dla `legacyAutoSortFn`. */
export function createLegacySortedRowModel() {
	const createModel = createSortedRowModel<DataTableFeaturesBase, RowData>();
	return (table: Parameters<typeof createModel>[0]) => {
		const getModel = createModel(table);
		return () => {
			const previous = activeAutoSort;
			activeAutoSort = { table: table as unknown as FilteredRowsSource, fns: new Map() };
			try {
				return getModel();
			} finally {
				activeAutoSort = previous;
			}
		};
	};
}

/* ============================================================================
   Pinning kolumn
   ============================================================================ */

function isStringArray(value: unknown): value is string[] {
	return Array.isArray(value) && value.every((id) => typeof id === "string");
}

/**
 * Sprowadza zapisany pinning do ksztaltu v9 `{ start, end }`.
 *
 * Przyjmuje format v8 `{ left, right }` (localStorage i schematy sprzed
 * migracji), aktualny `{ start, end }` oraz brak strony. Wartosc, ktorej nie
 * da sie odczytac jako list identyfikatorow kolumn, zastepuje `fallback`
 * zamiast wywracac tabele.
 */
export function normalizeColumnPinning(
	value: unknown,
	fallback: ColumnPinningState,
): ColumnPinningState {
	if (!value || typeof value !== "object" || Array.isArray(value)) return fallback;
	const raw = value as Record<string, unknown>;
	const start = raw.start !== undefined ? raw.start : raw.left;
	const end = raw.end !== undefined ? raw.end : raw.right;
	if (start === undefined && end === undefined) return fallback;
	if (start !== undefined && !isStringArray(start)) return fallback;
	if (end !== undefined && !isStringArray(end)) return fallback;
	return { start: start ? [...start] : [], end: end ? [...end] : [] };
}

/* ============================================================================
   Zestaw funkcji i typy
   ============================================================================ */

const baseFeatures = {
	columnFilteringFeature,
	columnOrderingFeature,
	columnPinningFeature,
	columnSizingFeature,
	columnResizingFeature,
	columnVisibilityFeature,
	rowSortingFeature,
	columnMeta: metaHelper<DataTableColumnMeta>(),
};

type DataTableFeaturesBase = typeof baseFeatures;

export const dataTableFeatures = tableFeatures({
	...baseFeatures,
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createLegacySortedRowModel(),
	// Nazwy wbudowanych funkcji v8 — `sortFn: "text"` itp. dzialaja jak przed migracja.
	sortFns: legacySortFns,
});

export type DataTableFeatures = typeof dataTableFeatures;

export type ColumnDef<TData extends RowData, TValue = unknown> = TanStackColumnDef<
	DataTableFeatures,
	TData,
	TValue
>;
export type Column<TData extends RowData, TValue = unknown> = TanStackColumn<
	DataTableFeatures,
	TData,
	TValue
>;
export type Row<TData extends RowData> = TanStackRow<DataTableFeatures, TData>;
export type { ColumnPinningState, RowData };
